import { TabsContext } from './Tabs';
import { Theme } from '@emotion/react';
import { Typography } from 'bento';
import { tabItemBase, tabItemLabel } from './tabItemCssDefs';
import React, { useContext } from 'react';

export type TabItemProps<C extends React.ElementType> = {
  /**
   * Required label, string to set the text of the Tab component
   */
  label: string;
  /**
   * Required value, string used to set the selected Tab on the Tabs component
   */
  value: string | number;
  /**
   * Optional as, React.ElementType this should be used to fix sematic issues only like when wrapping in a NavLink component. This will remove the role. To make this semantic please add the role on the NavLink or corresponding element.
   */
  component?: C;
  /**
   * Optional isSelected, boolean to set if this Tab is the currently selected tab. This will overwrite the context so do not use in conjunction with context
   */
  isSelected?: boolean;
  /**
   * Optional icon, React.ReactNode is the element for the Icon you want to append to your Tab
   */
  icon?: React.ReactNode;
} & React.ComponentProps<C>;

/**
 * @deprecated Deprecated — use pantry Tabs instead.
 * @see https://docs.tocktix.com/pantry?path=/docs/components-tabs--docs
 * Migration: Replace TabItem with the corresponding sub-component in pantry Tabs.
 */
export const TabItem = <C extends React.ElementType>({
  label,
  value,
  component,
  isSelected,
  icon,
  onClick,
  className,
  ...rest
}: TabItemProps<C>) => {
  const Component = component || 'button';

  const tabsContext = useContext(TabsContext);

  const clickHandler = () => {
    // Only allows state handler if controlled
    tabsContext?.setSelected(value);
    onClick?.();
  };

  const contextSelected = tabsContext?.selected === value;
  const isActive = isSelected ?? contextSelected;

  return (
    <Component
      css={(theme: Theme) => tabItemBase(theme, isActive)}
      role={'tab'}
      aria-selected={isActive}
      onClick={clickHandler}
      tabIndex={0}
      className={className}
      {...rest}
    >
      {icon}
      <Typography variant="bodyStrong" css={(theme: Theme) => tabItemLabel(theme, isActive)}>
        {label}
      </Typography>
    </Component>
  );
};
